import React from "react";
import { useIntl } from "react-intl";
import { CheckCircleFilled } from "@ant-design/icons";
import { useStyle } from "../../styles/uchat/UCIndependentChat.styles";
import { useTheme } from "./UCIndependentChat/hooks/useTheme";
import { getRatingDesc } from "./evaluationUtils";

// 评价等级配置（与评价时保持一致）
const RATING_CONFIG = {
  1: { text: "😠", color: "#ff4d4f", bgColor: "rgba(255, 77, 79, 0.1)" }, // red
  2: { text: "😔", color: "#ff7a45", bgColor: "rgba(255, 122, 69, 0.1)" }, // orange
  3: { text: "😐", color: "#ffc53d", bgColor: "rgba(255, 197, 61, 0.1)" }, // gold
  4: { text: "🙂", color: "#1890ff", bgColor: "rgba(24, 144, 255, 0.1)" }, // blue
  5: { text: "🥰", color: "#52c41a", bgColor: "rgba(82, 196, 26, 0.1)" }, // green
};

interface UCEvaluationCompletedProps {
  rating: number;
  feedback?: string;
}

const UCEvaluationCompleted: React.FC<UCEvaluationCompletedProps> = ({
  rating,
  feedback,
}) => {
  const intl = useIntl();
  const { colors } = useTheme();
  const { styles } = useStyle(colors);

  return (
    <div className={styles.evaluationCompletedCard}>
      {/* 成功图标和标题 */}
      <div className={styles.evaluationTitle}>
        <CheckCircleFilled className={styles.evaluationSuccessIcon} />
        {intl.formatMessage(
          { id: "evaluation.completed" },
          { defaultMessage: "评价已完成" }
        )}
      </div>

      {/* 评分展示 - 使用表情符号（与评价时保持一致） */}
      <div className={styles.evaluationStarsRow}>
        <div className={styles.evaluationRatingLabel}>
          {intl.formatMessage(
            { id: "evaluation.your.rating" },
            { defaultMessage: "您的评分" }
          )}
        </div>
        <div className={styles.evaluationStarsContainer}>
          {([1, 2, 3, 4, 5] as const).map((level) => {
            const config = RATING_CONFIG[level];
            const isSelected = rating === level;

            return (
              <span
                key={level}
                className={styles.evaluationEmoji}
                style={{
                  fontSize: "20px",
                  padding: "2px 4px",
                  border: `2px solid ${
                    isSelected ? config.color : colors.border || "#d9d9d9"
                  }`,
                  borderRadius: "6px",
                  background: isSelected
                    ? config.bgColor
                    : colors.messageBg || colors.background || "#fff",
                  display: "inline-block",
                }}
              >
                {config.text}
              </span>
            );
          })}
          <span className={styles.evaluationMetaBlue}>
            {rating}/5 - {getRatingDesc(intl, rating)}
          </span>
        </div>
      </div>

      {/* 反馈内容 */}
      {feedback && (
        <div className={styles.evaluationFeedbackBox}>
          <div className={styles.evaluationFeedbackLabel}>
            {intl.formatMessage(
              { id: "evaluation.your.feedback" },
              { defaultMessage: "您的反馈" }
            )}
          </div>
          <div className={styles.evaluationFeedbackText}>{feedback}</div>
        </div>
      )}

      {/* 感谢信息 */}
      <div className={styles.evaluationThanks}>
        {intl.formatMessage(
          { id: "evaluation.thank.you" },
          { defaultMessage: "感谢您的宝贵反馈，我们会持续改进服务！" }
        )}
      </div>
    </div>
  );
};

export default UCEvaluationCompleted;
